import type { ComponentChildren, ContextType, Ref, RefObject } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import type { FormVariantContext } from '#hooks/UNSAFE_useFormVariantContext';
import { useInputGroupContext } from '#hooks/UNSAFE_useInputGroupContext';
import { useTextFieldTooltip } from './useTextFieldTooltip';
import { useUser } from '#hooks/UNSAFE_useUser';
import { Flex } from '#UNSAFE_Flex';
import { isEmbeddedVariant } from '#utils/PRIVATE_formControlUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';
import type { TextFieldStyles, TextFieldVariantOptions } from './themes/TextFieldStyles.css';

type VariantProps = {
  contentVariant?: 'input' | 'textarea';
  statusVariant?: 'error' | 'none' | 'warning';
  styleVariant?: ContextType<typeof FormVariantContext>;
};

type Props = VariantProps & {
  mainContent?: ComponentChildren;
  insideLabel?: ComponentChildren;
  startContent?: ComponentChildren;
  endContent?: ComponentChildren;
  resize?: 'horizontal' | 'vertical' | 'both';
  rootRef?: Ref<HTMLDivElement>;
  hasZeroStartMargin?: boolean;
  labelText?: string;
};

// Renders the oj-text-field-container dom which includes the
// start, middle (where the inside label and inputElem goes),
// and end pieces of the form component.
// This does not include user assistance because that is rendered outside the
// oj-text-field-container; that is rendered in the TextField component.
export const TextFieldContent = ({
  contentVariant = 'input',
  insideLabel,
  labelText,
  mainContent,
  startContent,
  statusVariant = 'none',
  styleVariant = 'default',
  endContent,
  resize,
  rootRef,
  hasZeroStartMargin
}: Props) => {
  const { hasTruncatedLabel, isDisabled, isFocused, isLoading } = useFormFieldContext();
  const { direction } = useUser();
  const isLtr = direction === 'ltr';
  const hasInsideLabel = insideLabel !== undefined;
  const inputGroupContext = useInputGroupContext();
  const hasGroupContext = inputGroupContext !== null;
  // depending on browser direction, apply the appropriate styling.
  // when 'rtl' the flex row direction is reversed.
  const inputGroupPosition = inputGroupContext
    ? inputGroupContext.last
      ? isLtr
        ? 'groupRight'
        : 'groupLeft'
      : inputGroupContext.index === 0
      ? isLtr
        ? 'groupLeft'
        : 'groupRight'
      : 'groupMiddle'
    : undefined;

  const {
    variantClasses,
    styles: { textFieldContainerBase }
  } = useComponentTheme<TextFieldVariantOptions, TextFieldStyles>(TextFieldRedwoodTheme, {
    statusVariant,
    styleVariant: isEmbeddedVariant(styleVariant) ? 'embedded' : 'default',
    contentVariant,
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
    focused: isFocused ? 'isFocused' : 'notFocused',
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    loading: isLoading ? 'isLoading' : 'notLoading',
    resize: resize ?? 'none',
    withinGroup: hasGroupContext ? 'isWithinGroup' : undefined,
    inputGroupPosition
  });

  const { variantClasses: startContentStyles } = useComponentTheme<TextFieldVariantOptions>(
    TextFieldRedwoodTheme,
    {
      startContent: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
      startContentMargin: hasZeroStartMargin ? 'noStartMargin' : 'hasStartMargin',
      startContentDisabled: isDisabled ? 'isDisabled' : 'notDisabled'
    }
  );

  const {
    variantClasses: middleStyles,
    styles: { textFieldEndContent }
  } = useComponentTheme<TextFieldVariantOptions, TextFieldStyles>(TextFieldRedwoodTheme, {
    middleContent: contentVariant,
    textAreaResizeDirection: resize && contentVariant === 'textarea' ? direction : undefined
  });

  // Use an onKeyDownCapture handler to set a flag that closes the tooltip when any key is pressed.
  // Reset the flag when we see the user blur out of the input.
  const [keyDownCaptureKeyPressed, setKeyDownCaptureKeyPressed] = useState(false);
  const keyDownCaptureHandler = useCallback(() => {
    if (!keyDownCaptureKeyPressed && hasTruncatedLabel) {
      setKeyDownCaptureKeyPressed(true);
    }
  }, [hasTruncatedLabel, keyDownCaptureKeyPressed]);
  const blurHandler = useCallback(
    (event: FocusEvent) => {
      if (
        keyDownCaptureKeyPressed &&
        (event.relatedTarget == null ||
          !(event.currentTarget as HTMLElement).contains(event.relatedTarget as HTMLElement))
      ) {
        setKeyDownCaptureKeyPressed(false);
      }
    },
    [keyDownCaptureKeyPressed]
  );

  const fieldRef = useRef(null);
  const ref = rootRef ?? fieldRef;
  const { tooltipContent, tooltipEventHandlerProps } = useTextFieldTooltip({
    hasTruncatedLabel: hasTruncatedLabel && !keyDownCaptureKeyPressed,
    fieldRef: ref as RefObject<HTMLElement>,
    isDisabled: !hasInsideLabel || !!isDisabled,
    label: labelText
  });
  const eventHandlers =
    hasInsideLabel && !isDisabled
      ? { onBlur: blurHandler, onKeyDownCapture: keyDownCaptureHandler }
      : {};

  return (
    <div
      role="presentation"
      class={classNames([variantClasses, textFieldContainerBase])}
      ref={ref}
      {...tooltipEventHandlerProps}>
      {startContent && (
        <span class={startContentStyles}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {startContent}
          </Flex>
        </span>
      )}
      <div class={middleStyles} {...eventHandlers}>
        {insideLabel}
        {mainContent}
      </div>
      {endContent && (
        <span class={textFieldEndContent}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {endContent}
          </Flex>
        </span>
      )}
      {tooltipContent}
    </div>
  );
};
